import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import SlidingPane from 'react-sliding-pane';

import FilterPanel from '../../TableView/components/FilterPanel';
import DataTable from '../../DataTable/components/DataTable';
import XCloseIcon from '../../shared/Icons/XCloseIcon';
import ReceiptIcon from '../../shared/Icons/ReceiptIcon';
import StatusBadge from '../../../shared/StatusBadge';
import { EmptyState } from '@opendate-app/setlist';
import client from '../../../shared/api/client';

var ReactDOM = require('react-dom');
import $ from 'jquery';
require('datatables.net-bs4');

const decodeHtml = (html) => {
  var txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

const RefundRequestsPage = ({
  team,
  refundReasons,
  dataTableTitle,
  dataTableDataSource,
  dataTableOrder,
  dataTableColumns,
  filters: initialFilters,
}) => {
  const [dataTableEl, setDataTableEl] = useState(null);
  const [filters, setFilters] = useState(initialFilters || []);
  const [selectedRefundRequest, setSelectedRefundRequest] = useState(null);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [reason, setReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [denying, setDenying] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [confirmingDeny, setConfirmingDeny] = useState(false);
  const [denialNote, setDenialNote] = useState('');
  const activeStatusesRef = useRef([]);

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency: team.base_currency_symbol || 'USD' }),
    [team.base_currency_symbol],
  );

  const reduceFilters = (list) => list
    .filter((f) => f.value && f.value.length > 0)
    .reduce((params, f) => Object.assign(params, { [f.paramName]: f.value }), {});

  const reducedFilters = useMemo(() => reduceFilters(filters), [filters]);

  const updateFilter = (filter, value) => {
    setFilters((prev) => prev.map((item) => {
      if (item.paramName === filter.paramName) {
        const arrayValue = Array(value).flat();
        return { ...item, value: arrayValue };
      }
      return item;
    }));
  };

  const reloadTable = useCallback(() => {
    if (dataTableEl) {
      $(dataTableEl).DataTable().ajax.reload(null, false);
    }
  }, [dataTableEl]);

  const renderZeroRecordsState = useCallback((settings) => {
    if (settings.aoData.length) return;

    const mount = settings.nTable.getElementsByClassName('refund-requests-zero-records-mount')[0];
    if (!mount) return;

    const statuses = activeStatusesRef.current;
    ReactDOM.render((
      <div style={{ padding: '48px 24px' }}>
        <EmptyState
          icon={ReceiptIcon}
          title="No refund requests"
          description={(
            <>
              When a customer requests a refund on their order, it’ll show up here for you to review — approve the request to issue the refund, or deny it.
              {statuses.length > 0 && (
                <>
                  {' '}
                  Showing {statuses.join(', ').toLowerCase()} requests. Change the Status filter above to see others.
                </>
              )}
            </>
          )}
        />
      </div>
    ), mount);
  }, []);

  const closePane = useCallback(() => {
    setSelectedRefundRequest(null);
    setSelectedTicketIds([]);
    setReason('');
    setErrors([]);
    setConfirmingDeny(false);
    setDenialNote('');
  }, []);

  const handleApprove = useCallback(() => {
    if (!selectedRefundRequest) return;
    if (!reason) {
      setErrors(['Please select a refund reason.']);
      return;
    }
    if (selectedTicketIds.length === 0) {
      setErrors(['Select at least one ticket to refund.']);
      return;
    }
    setApproving(true);
    setErrors([]);
    client.post(`/teams/${team.id}/refund_requests/${selectedRefundRequest.id}/approve.json`, {
      reason,
      ticket_ids: selectedTicketIds,
    })
      .then((res) => {
        if (!res?.data?.refund_request) throw new Error('Unexpected response');
        toast.success('Refund issued and request approved.');
        closePane();
        reloadTable();
      })
      .catch((err) => {
        const responseErrors = err.response?.data?.errors;
        setErrors(responseErrors?.length ? responseErrors : ['Something went wrong. Please try again.']);
      })
      .finally(() => setApproving(false));
  }, [team.id, selectedRefundRequest, reason, selectedTicketIds, closePane, reloadTable]);

  const handleDeny = useCallback(() => {
    if (!selectedRefundRequest) return;
    if (!confirmingDeny) {
      setConfirmingDeny(true);
      return;
    }
    setDenying(true);
    client.post(`/teams/${team.id}/refund_requests/${selectedRefundRequest.id}/deny.json`, {
      denial_note: denialNote,
    })
      .then((res) => {
        if (!res?.data?.refund_request) throw new Error('Unexpected response');
        toast.success(res.data.emailed_customer
          ? 'Refund request denied. An email is on its way.'
          : 'Refund request denied. No email was sent.');
        closePane();
        reloadTable();
      })
      .catch((err) => {
        const responseErrors = err.response?.data?.errors;
        toast.error(responseErrors?.length ? responseErrors.join(', ') : 'Failed to deny request.');
      })
      .finally(() => setDenying(false));
  }, [team.id, selectedRefundRequest, confirmingDeny, denialNote, closePane, reloadTable]);

  const handleResolve = useCallback(() => {
    if (!selectedRefundRequest) return;
    setResolving(true);
    setErrors([]);
    client.post(`/teams/${team.id}/refund_requests/${selectedRefundRequest.id}/resolve.json`, {})
      .then((res) => {
        if (!res?.data?.refund_request) throw new Error('Unexpected response');
        toast.success('Request marked resolved. No email was sent.');
        closePane();
        reloadTable();
      })
      .catch((err) => {
        const responseErrors = err.response?.data?.errors;
        setErrors(responseErrors?.length ? responseErrors : ['Something went wrong. Please try again.']);
      })
      .finally(() => setResolving(false));
  }, [team.id, selectedRefundRequest, closePane, reloadTable]);

  const handleRowCallback = useCallback((row, data) => {
    const openDrawer = () => {
      const rowData = JSON.parse(decodeHtml(data.json));
      setSelectedRefundRequest(rowData);
      const eligibleIds = (rowData.tickets || [])
        .filter((t) => t.eligible)
        .map((t) => t.id);
      setSelectedTicketIds(eligibleIds);
      setReason('');
      setErrors([]);
    };
    row.tabIndex = 0;
    row.setAttribute('role', 'button');
    row.addEventListener('click', openDrawer);
    row.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDrawer();
      }
    });
  }, []);

  const toggleTicket = (ticketId) => {
    setSelectedTicketIds((prev) => (
      prev.includes(ticketId) ? prev.filter((id) => id !== ticketId) : [...prev, ticketId]
    ));
  };

  const tickets = selectedRefundRequest?.tickets || [];
  const eligibleTickets = tickets.filter((t) => t.eligible);
  const refundSubtotal = eligibleTickets
    .filter((t) => selectedTicketIds.includes(t.id))
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  // Per-ticket amounts round each 3-decimal charge up to a cent, so a full
  // selection can sum past what was charged — mirror the server's clamp
  // (RefundRequest#clamp_to_refundable_remainder) so the total shown is the
  // total the approval refunds.
  const refundTotal = Math.min(refundSubtotal, selectedRefundRequest?.refundable_ceiling ?? Infinity);

  const isPending = selectedRefundRequest?.status === 'pending';
  const submitting = approving || denying || resolving;
  const nothingRefundable = eligibleTickets.length === 0;
  const canResolve = selectedRefundRequest?.resolvable === true;
  const needsFinanceMessage = selectedRefundRequest?.needs_finance_message;

  const statusFilter = filters.find((f) => f.paramName === 'status');
  const activeStatuses = (statusFilter && statusFilter.value) || [];
  activeStatusesRef.current = activeStatuses;

  return (
    <div>
      <ToastContainer icon={false} />
      <FilterPanel
        filters={filters}
        clearFilterValues={() => {
          setFilters((prev) => prev.map((item) => ({ ...item, value: [] })));
        }}
        debouncedUpdateFilter={updateFilter}
        updateFilter={updateFilter}
      />
      <DataTable
        title={dataTableTitle}
        dataSource={dataTableDataSource}
        columns={dataTableColumns}
        onTableElementSet={(el) => setDataTableEl(el)}
        rowCallback={handleRowCallback}
        reducedFilters={reducedFilters}
        order={dataTableOrder}
        zeroRecords={"<span class='refund-requests-zero-records-mount'></span>"}
        drawCallback={renderZeroRecordsState}
      />
      <SlidingPane
        isOpen={!!selectedRefundRequest}
        className="sliding-pane-container-full-height refund-request-pane"
        overlayClassName="refund-request-pane-overlay"
        width={'420px'}
        hideHeader={true}
        onRequestClose={closePane}
      >
        <style>{`
          /* Triple class out-specifies the library's z-index:2 rule so the
             backdrop covers the fixed nav rail (z-index 1030 in AppMenuV2). */
          .refund-request-pane-overlay.slide-pane__overlay.ReactModal__Overlay--after-open { z-index: 1050; }
          .rr-btn-destructive { background-color: var(--sl-bg-error-solid) !important; }
          .rr-btn-destructive:hover { background-color: var(--sl-bg-error-solid-hover) !important; }
          .rr-drawer-close { color: var(--sl-fg-quaternary); }
          .rr-drawer-close:hover { background: var(--sl-bg-tertiary); color: var(--sl-text-secondary); }
        `}</style>
        {selectedRefundRequest && (
          <>
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ padding: '16px 24px', borderBottom: '1px solid var(--sl-border-secondary)' }}
            >
              <span style={{ fontSize: '18px', lineHeight: '28px', fontWeight: 600, color: 'var(--sl-text-primary)' }}>
                Refund Request
              </span>
              <button
                type="button"
                onClick={closePane}
                className="rr-drawer-close d-flex align-items-center justify-content-center"
                aria-label="Close"
                style={{ width: '40px', height: '40px', marginRight: '-8px', borderRadius: '8px', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
              >
                <XCloseIcon width="20" height="20" />
              </button>
            </div>
            <div className="row m-0">
              <div className="col-12" style={{ marginTop: '20px' }}>
                <div className="card border-0" style={{ background: 'var(--sl-bg-secondary)', borderRadius: '12px' }}>
                  <div className="card-body" style={{ padding: '12px 24px 0px 24px' }}>
                    <table className="table table-sm table-borderless">
                      <tbody>
                        <tr style={{ fontSize: '14px' }}>
                          <td><strong>Request details</strong></td>
                          <td className="text-right">
                            <StatusBadge status={selectedRefundRequest.status} />
                          </td>
                        </tr>
                        {selectedRefundRequest.event_id ? (
                          <tr style={{ fontSize: '12px' }}>
                            <td>Event</td>
                            <td className="text-right overflow-ellipsis">
                              <a href={`/teams/${selectedRefundRequest.team_id}/confirms/${selectedRefundRequest.event_id}/dashboard`} target="_blank" rel="noopener noreferrer">
                                {selectedRefundRequest.event_name}
                              </a>
                            </td>
                          </tr>
                        ) : null}
                        <tr style={{ fontSize: '12px' }}>
                          <td>Order ID</td>
                          <td className="text-right">#{selectedRefundRequest.order_id}</td>
                        </tr>
                        {needsFinanceMessage ? null : (
                          <tr style={{ fontSize: '12px' }}>
                            <td>Amount</td>
                            <td className="text-right">{currencyFormatter.format(selectedRefundRequest.refundable_total || 0)}</td>
                          </tr>
                        )}
                        <tr style={{ fontSize: '12px' }}>
                          <td>Customer</td>
                          <td className="text-right">
                            <div>{selectedRefundRequest.customer_name}</div>
                            <div className="small text-muted">{selectedRefundRequest.customer_email}</div>
                          </td>
                        </tr>
                        <tr style={{ fontSize: '12px' }}>
                          <td>Customer reason</td>
                          <td className="text-right">{selectedRefundRequest.reason || '—'}</td>
                        </tr>
                        <tr style={{ fontSize: '12px' }}>
                          <td>Requested</td>
                          <td className="text-right">{selectedRefundRequest.requested_on}</td>
                        </tr>
                        {selectedRefundRequest.refund_status ? (
                          <tr style={{ fontSize: '12px' }}>
                            <td>Refund status</td>
                            <td className="text-right">
                              <StatusBadge status={selectedRefundRequest.refund_status} />
                            </td>
                          </tr>
                        ) : null}
                        {selectedRefundRequest.decided_on ? (
                          <tr style={{ fontSize: '12px' }}>
                            <td>Decided by</td>
                            <td className="text-right">
                              <div>{selectedRefundRequest.decided_by || '—'}</div>
                              <div className="small text-muted">{selectedRefundRequest.decided_on}</div>
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            {isPending && (
              <div className="row m-0" style={{ marginTop: '20px' }}>
                <div className="col-12">
                  <form onSubmit={(e) => { e.preventDefault(); handleApprove(); }}>
                    <p className="mb-1" style={{ fontSize: '16px' }}>
                      <strong>
                        {needsFinanceMessage
                          ? 'A finance admin needs to handle this'
                          : nothingRefundable ? 'Close out this request' : 'Approve this request'}
                      </strong>
                    </p>
                    <p className="small mb-3">
                      {needsFinanceMessage || (nothingRefundable
                        ? 'No tickets on this order can be refunded through this request.'
                        : `Select the tickets to refund for ${selectedRefundRequest.customer_email}.`)}
                    </p>

                    {needsFinanceMessage ? null : tickets.length === 0 ? (
                      <p className="small text-muted">
                        {selectedRefundRequest.dedicated_account_donation
                          ? 'This order is a donation charged to your dedicated donation account. Refund it from the Stripe dashboard — it can’t be refunded here.'
                          : 'No tickets found on this order.'}
                      </p>
                    ) : (
                      <div className="mb-3" style={{ border: '1px solid var(--sl-border-secondary)', borderRadius: '8px' }}>
                        {tickets.map((ticket) => {
                          const checked = selectedTicketIds.includes(ticket.id);
                          return (
                            <label
                              key={ticket.id}
                              htmlFor={`refund-ticket-${ticket.id}`}
                              className="d-flex align-items-center justify-content-between mb-0"
                              style={{
                                padding: '8px 12px',
                                borderBottom: '1px solid var(--sl-border-secondary)',
                                cursor: ticket.eligible ? 'pointer' : 'not-allowed',
                                opacity: ticket.eligible ? 1 : 0.55,
                              }}
                            >
                              <div className="d-flex align-items-start" style={{ minWidth: 0 }}>
                                <input
                                  type="checkbox"
                                  id={`refund-ticket-${ticket.id}`}
                                  className="mr-2"
                                  checked={checked}
                                  disabled={!ticket.eligible || submitting}
                                  onChange={() => toggleTicket(ticket.id)}
                                />
                                <span className="small text-wrap">
                                  {ticket.label}
                                  {!ticket.eligible && (
                                    <span className="text-muted"> · not refundable</span>
                                  )}
                                </span>
                              </div>
                              <span className="small ml-2"><strong>{currencyFormatter.format(ticket.amount || 0)}</strong></span>
                            </label>
                          );
                        })}
                        <div
                          className="d-flex justify-content-between align-items-center small"
                          style={{ padding: '8px 12px', background: 'var(--sl-bg-secondary)', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}
                        >
                          <span>Refund total</span>
                          <strong>{currencyFormatter.format(refundTotal)}</strong>
                        </div>
                      </div>
                    )}

                    {!nothingRefundable && !needsFinanceMessage && (
                      <div className="form-group">
                        <label
                          htmlFor="refund-request-reason"
                          className="text-dark"
                          style={{ fontSize: '14px' }}
                        >
                          <strong>Refund reason (required)</strong>
                        </label>
                        <select
                          id="refund-request-reason"
                          className="form-control form-control-sm"
                          style={{ border: '1px solid var(--sl-border-primary)', borderRadius: '8px' }}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          disabled={submitting}
                        >
                          <option value="">— Select a reason —</option>
                          {refundReasons.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {errors.length > 0 && (
                      <div className="small" style={{ color: 'var(--sl-text-error)', marginBottom: '12px' }}>
                        {errors.map((e, i) => <div key={i}>{e}</div>)}
                      </div>
                    )}
                    {needsFinanceMessage ? null : nothingRefundable ? (canResolve && (
                      <>
                        <p className="small text-muted mb-2">
                          Resolving closes the request without emailing {selectedRefundRequest.customer_email}.
                        </p>
                        <button
                          type="button"
                          className="btn btn-setlist-primary"
                          style={{ width: '100%' }}
                          onClick={handleResolve}
                          disabled={submitting}
                        >
                          {resolving ? 'Resolving…' : 'Mark resolved'}
                        </button>
                      </>
                    )) : (
                      <button
                        type="submit"
                        className="btn btn-setlist-primary"
                        style={{ width: '100%' }}
                        disabled={submitting || !reason || selectedTicketIds.length === 0}
                      >
                        {approving ? 'Processing…' : 'Approve & Refund'}
                      </button>
                    )}
                    {needsFinanceMessage ? null : confirmingDeny ? (
                      <div className="mt-3">
                        <div className="form-group mb-2">
                          <label
                            htmlFor="refund-request-denial-note"
                            className="text-dark"
                            style={{ fontSize: '14px' }}
                          >
                            <strong>Note to the customer (optional)</strong>
                          </label>
                          <textarea
                            id="refund-request-denial-note"
                            className="form-control form-control-sm"
                            style={{ border: '1px solid var(--sl-border-primary)', borderRadius: '8px' }}
                            rows={3}
                            maxLength={2000}
                            value={denialNote}
                            onChange={(e) => setDenialNote(e.target.value)}
                            disabled={submitting}
                            placeholder="Included in the email letting them know the request was not approved, when one is sent."
                          />
                        </div>
                        <div className="d-flex" style={{ gap: '8px' }}>
                          <button
                            type="button"
                            className="btn btn-setlist-secondary"
                            onClick={() => { setConfirmingDeny(false); setDenialNote(''); }}
                            disabled={submitting}
                            style={{ flex: 1 }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-setlist-primary rr-btn-destructive"
                            onClick={handleDeny}
                            disabled={submitting}
                            style={{ flex: 1 }}
                          >
                            {denying ? 'Denying…' : 'Confirm deny'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-setlist-secondary"
                        style={{ width: '100%', marginTop: '8px' }}
                        onClick={handleDeny}
                        disabled={submitting}
                      >
                        Deny
                      </button>
                    )}
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </SlidingPane>
    </div>
  );
};

RefundRequestsPage.propTypes = {
  team: PropTypes.object.isRequired,
  refundReasons: PropTypes.array.isRequired,
  dataTableTitle: PropTypes.string.isRequired,
  dataTableDataSource: PropTypes.string.isRequired,
  dataTableOrder: PropTypes.array,
  dataTableColumns: PropTypes.array.isRequired,
  filters: PropTypes.array,
};

export default RefundRequestsPage;
